import * as React from "react";
import { useGridApiContext } from "@mui/x-data-grid";
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { Button, Menu, MenuItem } from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Function to translate specific column values based on header name
const translateColumns = (
  columnHeader: string,
  value: unknown,
  t: (key: string) => string,
) => {
  if (columnHeader === t("meeting.status")) {
    switch (value) {
      case "notstarted":
        return t("item_status.notstarted");
      case "inprogress":
        return t("item_status.inprogress");
      case "completed":
        return t("item_status.completed");
      case "cancelled":
        return t("item_status.cancelled");
      case "onhold":
        return t("item_status.onhold");
      case "draft":
        return t("meeting_status.draft");
      case "inreview":
        return t("meeting_status.inreview");
      default:
        return value;
    }
  }
  if (columnHeader?.toLowerCase() === t("item.types").toLowerCase()) {
    switch (String(value).toLowerCase().trim()) {
      case "task":
        return t("item.taskType");
      case "decision":
        return t("item.decisionType");
      case "info":
        return t("item.informationType");
      case "task,decision":
        return t("item.taskType") + ", " + t("item.decisionType");
      case "task,info":
        return t("item.taskType") + ", " + t("item.informationType");
      case "decision,info":
        return t("item.decisionType") + ", " + t("item.informationType");
      case "task,decision,info":
        return (
          t("item.taskType") +
          ", " +
          t("item.decisionType") +
          ", " +
          t("item.informationType")
        );
      default:
        return value;
    }
  }
  return value;
};

export interface CustomExportProps {
  apiRef?: React.MutableRefObject<GridApiCommunity>;
  printOptions?: {
    printHeader?: string;
    hiddenColumns?: string[];
  };
  csvOptions?: {
    delimiter?: string;
    includeHeaders?: boolean;
    customLines?: string[];
    exportFileName?: string;
  };
}

export function CustomExport(props: CustomExportProps) {
  return <GridContextExportMenu {...props} />;
}

/** Wrapper that pulls apiRef from the DataGrid context */
function GridContextExportMenu(props: CustomExportProps) {
  const apiRef = useGridApiContext();
  return (
    <ExportMenuBase
      {...props}
      apiRef={apiRef as React.MutableRefObject<GridApiCommunity>}
    />
  );
}

/**
 * Standalone export button that accepts an external apiRef.
 * Use this when rendering the export button outside the DataGrid context.
 */
export function StandaloneExport(
  props: CustomExportProps & {
    apiRef: React.MutableRefObject<GridApiCommunity>;
  },
) {
  return <ExportMenuBase {...props} />;
}

function ExportMenuBase({
  apiRef,
  printOptions,
  csvOptions,
}: CustomExportProps & { apiRef: React.MutableRefObject<GridApiCommunity> }) {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const getVisibleRows = () => {
    // Use sorted row IDs to respect current grid sorting
    const sortedRowIds = apiRef.current.getSortedRowIds
      ? apiRef.current.getSortedRowIds()
      : Array.from(apiRef.current.getRowModels().keys());
    const allRows = sortedRowIds.map((id) => apiRef.current.getRow(id));
    const filterModel = apiRef.current.state.filter?.filterModel;
    if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
      return allRows;
    }
    // Apply filtering manually
    return allRows.filter((row) => {
      return filterModel.items.every((filter) => {
        if (filter.value === undefined || filter.field === undefined)
          return true;
        let cellValue = row[filter.field];
        let filterValue = filter.value;

        // Get column definition
        const columnDef = getVisibleColumns().find(
          (c) => c.field === filter.field,
        );
        const headerLower = columnDef?.headerName?.toLowerCase() || "";

        // Translate for status columns
        if (headerLower.includes("status")) {
          cellValue = translateColumns(columnDef.headerName, cellValue, t);
          filterValue = translateColumns(columnDef.headerName, filterValue, t);
        }

        // Handle date columns (created/due date)
        const isDateCol =
          headerLower.includes("date") ||
          headerLower.includes("created") ||
          headerLower.includes("due");
        if (isDateCol) {
          const cellDate = cellValue ? new Date(cellValue) : undefined;
          const filterDate = filterValue ? new Date(filterValue) : undefined;
          switch (filter.operator) {
            case "is":
            case "equals":
              if (!cellDate || !filterDate) return false;
              return (
                cellDate.toISOString().slice(0, 10) ===
                filterDate.toISOString().slice(0, 10)
              );
            case "is not":
            case "not":
              if (!cellDate || !filterDate) return false;
              return (
                cellDate.toISOString().slice(0, 10) !==
                filterDate.toISOString().slice(0, 10)
              );
            case "is after":
              if (!cellDate || !filterDate) return false;
              return cellDate > filterDate;
            case "is on or after":
              if (!cellDate || !filterDate) return false;
              return cellDate >= filterDate;
            case "is before":
              if (!cellDate || !filterDate) return false;
              return cellDate < filterDate;
            case "is on or before":
              if (!cellDate || !filterDate) return false;
              return cellDate <= filterDate;
            case "is empty":
            case "blank":
              return !cellValue;
            case "is not empty":
            case "notBlank":
              return !!cellValue;
            default:
              // fallback to previous logic for other operators
              if (!cellDate || !filterDate) return true;
              switch (filter.operator) {
                case "contains":
                  return cellDate.toISOString().includes(String(filterValue));
                case "notContains":
                  return !cellDate.toISOString().includes(String(filterValue));
                case "startsWith":
                  return cellDate.toISOString().startsWith(String(filterValue));
                case "endsWith":
                  return cellDate.toISOString().endsWith(String(filterValue));
                case "greaterThan":
                  return cellDate > filterDate;
                case "lessThan":
                  return cellDate < filterDate;
                case "greaterThanOrEqual":
                  return cellDate >= filterDate;
                case "lessThanOrEqual":
                  return cellDate <= filterDate;
                default:
                  return true;
              }
          }
        }

        // Support all common operators for other columns
        switch (filter.operator) {
          case "contains":
            return String(cellValue)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          case "notContains":
            return !String(cellValue)
              .toLowerCase()
              .includes(String(filterValue).toLowerCase());
          case "equals":
          case "is":
            return (
              String(cellValue).toLowerCase() ===
              String(filterValue).toLowerCase()
            );
          case "not":
          case "notEquals":
            return (
              String(cellValue).toLowerCase() !==
              String(filterValue).toLowerCase()
            );
          case "startsWith":
            return String(cellValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase());
          case "endsWith":
            return String(cellValue)
              .toLowerCase()
              .endsWith(String(filterValue).toLowerCase());
          case "greaterThan":
            return cellValue > filterValue;
          case "lessThan":
            return cellValue < filterValue;
          case "greaterThanOrEqual":
            return cellValue >= filterValue;
          case "lessThanOrEqual":
            return cellValue <= filterValue;
          case "blank":
            return !cellValue;
          case "notBlank":
            return !!cellValue;
          default:
            return true;
        }
      });
    });
  };

  // Get only the columns currently visible (selected in MUI toolbar)
  const getVisibleColumns = () => {
    // getVisibleColumns is available in DataGridPro, fallback for DataGrid
    if (apiRef.current.getVisibleColumns) {
      return apiRef.current
        .getVisibleColumns()
        .filter((c: any) => c.field !== "__check__");
    }
    // fallback: filter columns with hide property
    return apiRef.current
      .getAllColumns()
      .filter((c: any) => c.field !== "__check__" && !c.hide);
  };

  const formatCellValue = (value: unknown, columnHeader?: string) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean")
      return value ? t("toolbar.export.true") : " ";
    if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
    ) {
      return value.slice(0, 10);
    }

    if (columnHeader) {
      return translateColumns(columnHeader, value, t);
    }
    return value;
  };

  const escapeCsvValue = (value: unknown, delimiter: string): string => {
    if (value === null || value === undefined) return "";
    let str = String(value);
    str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  //CSV Export
  const handleCsvExport = () => {
    if (!csvOptions) return;

    const {
      delimiter = ";",
      includeHeaders = true,
      customLines = [],
      exportFileName = "Pinnacle Export",
    } = csvOptions;

    const rows = getVisibleRows();
    const columns = getVisibleColumns();

    const bom = "\uFEFF";

    const header = includeHeaders
      ? columns.map((c) => `"${c.headerName}"`).join(delimiter)
      : "";

    const body = rows
      .map((r) =>
        columns
          .map((c) =>
            escapeCsvValue(
              formatCellValue(r[c.field], c.headerName),
              delimiter,
            ),
          )
          .join(delimiter),
      )
      .join("\n");

    const csv = [...customLines, header, body].filter(Boolean).join("\n");

    const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `${exportFileName}.csv`);
    link.click();

    handleClose();
  };

  //Print Export
  const handlePrint = () => {
    if (!printOptions) return;

    const { printHeader, hiddenColumns = [] } = printOptions;

    const rows = getVisibleRows();
    const columnsToPrint = getVisibleColumns().filter(
      (c) => !hiddenColumns.includes(c.headerName),
    );

    const tableHtml = `
      <table style="border-collapse: collapse; width:100%; table-layout: auto;">
        <thead>
          <tr>
            ${columnsToPrint
              .map(
                (c) => `
              <th style="
                border:1px solid #ccc;
                padding:8px;
                background:#f5f5f5;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 200px;
                text-align: left;
              ">
                ${c.headerName}
              </th>`,
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (r) => `
            <tr>
              ${columnsToPrint
                .map(
                  (c) => `
                <td style="
                  border:1px solid #ccc;
                  padding:8px;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  max-width: 200px;
                ">
                  ${formatCellValue(r[c.field], c.headerName)}
                </td>`,
                )
                .join("")}
            </tr>`,
            )
            .join("")}
        </tbody>
      </table>
    `;

    const html = `
      <html>
        <head>
          <title>${csvOptions?.exportFileName || "Export"}</title>
          <style>
            @page { size: A4 landscape; margin: 10mm; }
            body { font-family: sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>
          ${printHeader}<br />
          ${tableHtml}
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }

    handleClose();
  };

  return (
    <>
      {/* Export button and menu for CSV/Print */}
      <Button onClick={handleClick} startIcon={<FileDownload />} size="small">
        {t("toolbar.export.exportButton")}
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {csvOptions && (
          <MenuItem onClick={handleCsvExport}>
            <FileDownload fontSize="small" style={{ marginRight: 8 }} />
            {t("toolbar.export.csv")}
          </MenuItem>
        )}
        {printOptions && (
          <MenuItem onClick={handlePrint}>
            <Print fontSize="small" style={{ marginRight: 8 }} />
            {t("toolbar.export.print")}
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
